import { useEffect, useState } from "react";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sidebar } from "@/components/layout/Sidebar";
import { Topbar } from "@/components/layout/Topbar";
import { MockBanner } from "@/components/layout/MockBanner";
import { useSidebarMode } from "@/components/layout/useSidebarMode";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { DisconnectedBanner } from "@/features/notifications/components/DisconnectedBanner";
import { useNotificationsConnection } from "@/features/notifications/hooks/useNotificationsConnection";
import { cn } from "@/lib/utils/cn";

export function AppLayout() {
  const { t } = useTranslation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const accessToken = useAuthStore((s) => s.accessToken);
  const user = useAuthStore((s) => s.user);
  const refresh = useAuthStore((s) => s.refresh);
  const location = useLocation();
  const { collapsed, hidden, toggleCollapse, toggleHidden } = useSidebarMode();

  // Reidratação no cold load: a sessão persistida guarda só metadados (sem access
  // token em memória — ADR-0017). Sem isto a primeira chamada protegida tomaria 401
  // antes do interceptor renovar. Bloqueia o conteúdo protegido até o token voltar
  // (via cookie HttpOnly `rt` em POST /auth/refresh).
  const [booting, setBooting] = useState(isAuthenticated && !accessToken);
  useEffect(() => {
    if (isAuthenticated && !accessToken) {
      setBooting(true);
      void refresh().finally(() => setBooting(false));
    } else {
      setBooting(false);
    }
    // roda uma vez na montagem
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Liga o stream SSE de notificações (uma vez, acima das rotas autenticadas).
  useNotificationsConnection();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (booting) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg text-[13px] text-ink-muted">
        {t("common.loadingSession")}
      </div>
    );
  }

  // Gate de onboarding (backstop p/ acesso direto por URL): só entra no portal a
  // imobiliária com cadastro + termos concluídos E aprovada pelo backoffice.
  if (user?.profileCompleted === false) {
    return <Navigate to="/onboarding/agency-profile" replace />;
  }
  if (user?.termsAccepted === false) {
    return <Navigate to="/onboarding/terms" replace />;
  }
  if (user && user.approvalStatus !== "approved") {
    return <Navigate to="/onboarding/pending" replace />;
  }

  return (
    // zoom 0.85 reduz a densidade ~15% (alinhado a crm-web/launches-web/backoffice-web);
    // minHeight compensa o zoom pra a raiz (e a sidebar dark) preencherem a viewport.
    <div className="flex bg-bg" style={{ zoom: 0.85, minHeight: "calc(100vh / 0.85)" }}>
      {/* Desktop sidebar — wrapper anima width pra 0 quando hidden.
          height compensa o zoom (igual crm/launches) pra a sidebar dark
          preencher a viewport inteira, sem faixa branca no rodapé. */}
      <div
        className={cn(
          "sticky top-0 hidden shrink-0 transition-[width] duration-300 ease-out lg:block",
          hidden ? "w-0" : collapsed ? "w-[60px]" : "w-[228px]",
        )}
        style={{ height: "calc(100vh / 0.85)" }}
      >
        <div
          className={cn(
            "absolute inset-y-0 left-0 transition-transform duration-300 ease-out",
            hidden && "-translate-x-full",
          )}
        >
          <Sidebar collapsed={collapsed} onToggleCollapse={toggleCollapse} />
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={cn(
          "fixed inset-0 z-40 lg:hidden",
          drawerOpen ? "pointer-events-auto" : "pointer-events-none",
        )}
      >
        <div
          className={cn(
            "absolute inset-0 bg-black/50 transition-opacity",
            drawerOpen ? "opacity-100" : "opacity-0",
          )}
          onClick={() => setDrawerOpen(false)}
        />
        <div
          className={cn(
            "absolute inset-y-0 left-0 h-full transition-transform",
            drawerOpen ? "translate-x-0" : "-translate-x-full",
          )}
        >
          <Sidebar onItemClick={() => setDrawerOpen(false)} />
        </div>
      </div>

      <div className="flex flex-1 flex-col min-w-0">
        <MockBanner />
        <DisconnectedBanner />
        <Topbar
          onMenuClick={() => setDrawerOpen(true)}
          sidebarHidden={hidden}
          onToggleSidebar={toggleHidden}
        />
        <main className="flex-1 overflow-y-auto p-2.5 md:p-4 lg:p-5">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
